import { loadImage } from './image-preload';
import { roundRect } from './canvas.utils';
import { Capacitor } from '@capacitor/core';
import { Media } from '@capacitor-community/media';
import { Directory, Filesystem } from '@capacitor/filesystem';

export interface DownloadOptions {
  noOverlays?: boolean;
  padding?: number;
  logoHeight?: number,
  logoBackgroundColor?: string;
  textFont?: string;
}

export async function doDownload(visualizedCanvas: HTMLCanvasElement, logoUrl: string | undefined, text: string, options?: DownloadOptions): Promise<void> {
  const downloadCanvas = document.createElement('canvas');
  const downloadContext = downloadCanvas.getContext('2d')!;
  downloadCanvas.width = visualizedCanvas.width;
  downloadCanvas.height = visualizedCanvas.height;

  // draw image
  downloadContext.drawImage(visualizedCanvas, 0, 0);

  // draw logo
  if (logoUrl && !options?.noOverlays) {
    await drawLogo(downloadContext, logoUrl, options);
  }

  // draw text
  if (!options?.noOverlays) {
    await drawText(downloadContext, text, options);
  }

  // trigger download
  triggerDownload(text, downloadCanvas.toDataURL());

  // clean up
  downloadCanvas.remove();
}

async function drawLogo(ctx: CanvasRenderingContext2D, logoUrl: string, options?: DownloadOptions) {
  const padding = options?.padding || 10.0;
  const logoHeight = options?.logoHeight || 75.0;
  const logoBackgroundColor = options?.logoBackgroundColor || '#FFFFFF';
  const logo = await loadImage({ url: logoUrl })

  const ratio = logo.width / logo.height;

  const croppedWidth = ratio * logoHeight;
  const logoX = ctx.canvas.width / 2 - (croppedWidth / 2);

  ctx.fillStyle = logoBackgroundColor;
  ctx.strokeStyle = logoBackgroundColor;
  roundRect(ctx, logoX - padding, 0, croppedWidth + (padding * 2), logoHeight + (padding * 2), 5, true, true);

  ctx.drawImage(logo, logoX, padding, croppedWidth, logoHeight);
}

function drawText(ctx: CanvasRenderingContext2D, text: string, options?: DownloadOptions) {
  const padding = options?.padding || 10.0;

  const primaryColor = window.getComputedStyle(document.body).getPropertyValue('--customer-primary-color') || '#000000';
  const primaryTextColor = window.getComputedStyle(document.body).getPropertyValue('--customer-primary-text-color') || '#FFFFFF';

  ctx.fillStyle = primaryColor;
  ctx.strokeStyle = primaryColor;
  ctx.font = options?.textFont || '18px Roboto, sans-serif';

  const textSize = ctx.measureText(text);
  const textHeight = parseInt(ctx.font);
  const textPosX = (ctx.canvas.width / 2) - (textSize.width / 2);
  const textPosY = ctx.canvas.height - textHeight / 2;

  roundRect(ctx, textPosX - padding, ctx.canvas.height - textHeight - (padding * 2), textSize.width + (padding * 2), textHeight + (padding * 2), 5, true, true);

  ctx.fillStyle = primaryTextColor;
  ctx.fillText(text, textPosX, textPosY - (padding / 2));
}

function triggerDownload(fileName: string, href: string): void {

  // Add .png to file name if not present
  if (!fileName.endsWith('.png')) {
    fileName += '.png';
  }

  if (Capacitor.isNativePlatform()) {
    triggerDownloadNative(href).then();
  } else {
    const downLoadLink = document.createElement('a');
    downLoadLink.download = fileName;
    downLoadLink.href = href;
    downLoadLink.click();

    downLoadLink.remove();
  }
}

// https://bosti.medium.com/ionic-5-capacitor-play-with-photos-and-media-to-phones-gallery-library-storage-and-camera-e0faf3a94a5a
async function triggerDownloadNative(href: string): Promise<void> {
  const platform = Capacitor.getPlatform();

  // Get or create the album
  const albumName = 'DoorVision';
  let albumIdentifier: string | undefined = '';
  try {
    if (platform === 'ios') {

      // Handle albums
      let albums = await Media.getAlbums();
      albumIdentifier = albums.albums.find(a => a.name === albumName)?.identifier || undefined;
      if (!albumIdentifier) {
        // Doesn't exist, create new album

        await Media.createAlbum({ name: albumName });
        albums = await Media.getAlbums();
        albumIdentifier = albums.albums.find(a => a.name === albumName)?.identifier;
      }
    } else {
      await Media.createAlbum({ name: albumName });
    }
  } catch (e) {
    console.error('error creating album', albumName);
  }

  const tempFile = await Filesystem.writeFile({
    path: 'capture_' + new Date().getTime() + ".png",
    data: href,
    directory: Directory.Cache
  });


  Media.savePhoto({
    path: tempFile.uri,
    album: platform === 'ios' ? albumIdentifier : albumName
  }).then(async result => {
    // @ts-ignore
    cordova.plugins.fileOpener2.open(
      result.filePath,
      'image/png'
    );
  }).catch(e => console.error('error occurred', e));
}
